import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Chip, Typography } from '@mui/material';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { PropertyForm } from '@modules/_shared/components/property-form/PropertyForm';
import {
  getEquipmentFeatures,
  mapBasicPropertyItems,
  mapConstructionItems,
  mapEnergyExtendedItems,
  mapEnergyItems,
  mapFloorItems,
  mapHVACItems,
  mapLandSurfaceItems,
  mapLocationItems,
  mapOrientationItems,
  mapParkingItems,
  mapPlotNumberItems,
  mapRentalIncomeItems,
  mapRentalStatusItems,
  mapRoomCountItems,
  mapSurfaceItems,
  mapUnitItems,
} from '@modules/_shared/utils/propertyItemMappers';
import { useLeadPermissions } from '@modules/lead-details/hooks/useLeadPermissions';
import type { LeadDetailsDrawer } from '@modules/lead-details/type';
import { useLocale } from '@shared/hooks/locale';

type LeadDetailsCardProps = {
  lead: LeadDetailsDrawer;
  onContactUpdated?: () => Promise<void> | void;
};

export const LeadDetailsCard = ({
  lead,
  onContactUpdated,
}: LeadDetailsCardProps) => {
  const { t, locale } = useLocale();
  const [showMore, setShowMore] = useState(false);
  const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = useState(false);
  const { canManageLead, restrictedAgentView } = useLeadPermissions({ lead });

  const property = lead.property;
  const isSpain = (property?.country_code ?? '') === 'ES';

  const handleEditProperty = () => {
    if (!property?.id || restrictedAgentView) {
      return;
    }
    setIsPropertyDrawerOpen(true);
  };

  const handleClosePropertyDrawer = useCallback(() => {
    setIsPropertyDrawerOpen(false);
    void onContactUpdated?.();
  }, [onContactUpdated]);

  useEffect(() => {
    if (restrictedAgentView) {
      setShowMore(false);
      setIsPropertyDrawerOpen(false);
    }
  }, [restrictedAgentView]);

  const allItems = useMemo(() => {
    const equipmentFeatures = getEquipmentFeatures(property, t);

    return [
      ...mapBasicPropertyItems(property, lead, t, isSpain, locale),
      ...mapLandSurfaceItems(property, t),
      ...mapPlotNumberItems(property, t),
      ...mapSurfaceItems(property, t, isSpain),
      ...mapRoomCountItems(property, t, isSpain),
      ...mapUnitItems(property, t),
      ...mapFloorItems(property, t),
      ...mapOrientationItems(property, t),
      ...mapParkingItems(property, t),
      ...mapHVACItems(property, t),
      ...mapEnergyItems(property, t),
      ...mapEnergyExtendedItems(property, t, locale),
      ...mapConstructionItems(property, t),
      ...mapLocationItems(property, t),
      ...mapRentalIncomeItems(property, t, locale),
      ...mapRentalStatusItems(property, t),
      ...(equipmentFeatures.length > 0
        ? [
            {
              label: t('Equipment & Features'),
              value: (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    justifyContent: 'flex-end',
                  }}
                >
                  {equipmentFeatures.map(feature => (
                    <Chip
                      key={feature}
                      label={feature}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              ),
              showByDefault: false,
            },
          ]
        : []),
    ];
  }, [lead, property, t, isSpain, locale]);

  const visibleItems = useMemo(
    () =>
      allItems.map(item => ({
        ...item,
        show: item.showByDefault || showMore,
      })),
    [allItems, showMore],
  );

  const hasMoreItems = allItems.some(item => !item.showByDefault);
  const displayedItems = visibleItems.filter(item => item.show);
  const canEditProperty = canManageLead;
  const showEditButton = Boolean(
    !restrictedAgentView && canEditProperty && property?.id,
  );

  return (
    <>
      <ListingAccordion
        title={t('Lead details')}
        defaultExpanded
        disableCollapse
        disablePadding
        onEdit={showEditButton ? handleEditProperty : undefined}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {displayedItems.map((item, index) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                px: 2,
                py: 1,
                backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                borderRadius: '3px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
              {typeof item.value === 'string' ? (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    color: 'text.secondary',
                  }}
                >
                  {item.value}
                </Typography>
              ) : (
                item.value
              )}
            </Box>
          ))}
          {hasMoreItems && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.5,
                mb: 1,
              }}
            >
              <Button
                onClick={() => {
                  setShowMore(!showMore);
                }}
                size="small"
                sx={{
                  px: 1.5,
                  py: 0.25,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                disabled={restrictedAgentView}
              >
                {showMore ? t('Show less') : t('Show more')}
              </Button>
            </Box>
          )}
        </Box>
      </ListingAccordion>
      {canEditProperty && !restrictedAgentView && isPropertyDrawerOpen && (
        <Drawer
          onClose={handleClosePropertyDrawer}
          title={t('Edit property')}
          maxWidth={960}
        >
          <PropertyForm
            propertyId={property?.id}
            onSave={handleClosePropertyDrawer}
            onCancel={handleClosePropertyDrawer}
          />
        </Drawer>
      )}
    </>
  );
};
